import { useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
	GIFT_STATE,
	PIN_STATE,
	createGiftClaim,
	createPinClaim,
	declinePinClaim,
	fetchMilestoneAssignments,
	loadCombinedMilestoneClaims,
} from './milestoneClaims.js';

/**
 * Loads the user's milestone assignments and, for milestones with both a pin
 * and a gift, the claim status of each reward.
 */
const useMilestoneClaims = (currentUser) => {
	const client = useApolloClient();
	const [assignments, setAssignments] = useState([]);
	const [claims, setClaims] = useState([]);
	const [isLoaded, setIsLoaded] = useState(false);
	const hasLoaded = useRef(false);

	const userId = currentUser?.id;
	const companyId = currentUser?.company?.id;

	useEffect(() => {
		if (hasLoaded.current || !userId || !companyId) return;
		hasLoaded.current = true;

		const load = async () => {
			try {
				const milestoneAssignments = await fetchMilestoneAssignments(
					client,
					userId
				);
				setAssignments(milestoneAssignments);
				setClaims(
					await loadCombinedMilestoneClaims({
						client,
						currentUser,
						assignments: milestoneAssignments,
					})
				);
			} catch (error) {
				console.error('Error loading milestone claims:', error);
			} finally {
				setIsLoaded(true);
			}
		};

		load();
	}, [userId, companyId]);

	const updateClaim = (claimId, update) => {
		setClaims((prev) =>
			prev.map((claim) => (claim.id === claimId ? update(claim) : claim))
		);
	};

	// Returns the Snappy checkout link for the pin, if there is one
	const claimPin = async (claim) => {
		const transaction = await createPinClaim({
			client,
			currentUser,
			reward: claim.pin.reward,
		});
		updateClaim(claim.id, (c) => ({
			...c,
			pin: {
				...c.pin,
				transaction,
				link: transaction?.link || null,
				state: transaction?.link ? PIN_STATE.STARTED : PIN_STATE.CLAIMED,
			},
		}));
		return transaction?.link || null;
	};

	const declinePin = async (claim) => {
		await declinePinClaim({ client, currentUser, reward: claim.pin.reward });
		updateClaim(claim.id, (c) => ({
			...c,
			pin: { ...c.pin, state: PIN_STATE.DECLINED },
		}));
	};

	// Creates the Snappy gift link if needed and returns it
	const claimGift = async (claim) => {
		const link = claim.gift.link || (await createGiftClaim({ currentUser, claim }));
		if (link) markGiftStarted(claim, link);
		return link;
	};

	// The employee has opened the gift in Snappy; Snappy confirms it on the next load
	const markGiftStarted = (claim, link = claim.gift.link) => {
		updateClaim(claim.id, (c) => ({
			...c,
			gift: {
				...c.gift,
				link,
				state:
					c.gift.state === GIFT_STATE.CLAIMED ? c.gift.state : GIFT_STATE.STARTED,
			},
		}));
	};

	return {
		assignments,
		claims,
		isLoaded,
		claimPin,
		declinePin,
		claimGift,
		markGiftStarted,
	};
};

export default useMilestoneClaims;
